import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type CollisionDetection,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  TodoBoardResponse,
  TodoCardView,
  TodoColumnValue,
  TodoSourceView,
} from "@portal/schemas";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  cn,
} from "@portal/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  FolderCog,
  Link2Off,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  SquareArrowOutUpRight,
  Trash2,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import backlogIcon from "../../assets/backlog.png";
import {
  createTodoCard,
  deleteTodoCard,
  disconnectBacklog,
  moveTodoCard,
  syncTodoBoard,
  updateTodoCard,
} from "../../api/client";
import { TODO_BOARD_KEY, useTodoBoard } from "../../api/queries";
import {
  TODO_COLUMNS,
  cardsOf,
  columnDropId,
  columnLabel,
  dropTarget,
  dueLabel,
  jstToday,
  moveCard,
} from "../../features/todos/board";
import { BacklogProjectsDialog } from "../../features/todos/BacklogProjectsDialog";
import { TodoCardDialog } from "../../features/todos/TodoCardDialog";
import { HomeSection } from "../HomeSection";
import { InlineError } from "../InlineError";
import { CardStatus } from "./CardStatus";

/**
 * 落とし先の判定。マウスが指している列・カードを優先し、どこも指していないときだけ、いちばん近いものにする
 * (dnd-kit で複数の列を持つときの定番)。四隅の近さだけで決めると、列の幅が狭いとき(3 列のホーム。2026-10-14)、
 * 空の列の上へ運んでも、縦に長い列の角より隣の列のカードのほうが「近い」となり、元の列に戻っていた。
 */
const collisionDetection: CollisionDetection = (args) => {
  const pointed = pointerWithin(args);
  return pointed.length > 0 ? pointed : closestCorners(args);
};

const time = new Intl.DateTimeFormat("ja-JP", {
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "Asia/Tokyo",
});

/**
 * ToDo ボード(SPEC §10)。カンバンの 3 列(未着手 / 進行中 / 完了)に、手書きのカードと、
 * 取り込み元(Backlog の担当課題)から自動で入ったカードを並べる。ドラッグ(キーボードも可)か
 * カードのメニューで列を移す。列はポータル独自で、Backlog の状態は変えない。
 * 追加は列ごとの「＋」から。フォームは共通で、状態がその列で始まる(ユーザー要望 2026-10-13)。
 */
export function TodoBoardCard({ className }: { className?: string }) {
  const q = useTodoBoard();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{
    open: boolean;
    card: TodoCardView | null;
    column: TodoColumnValue;
  }>({ open: false, card: null, column: "TODO" });
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<TodoColumnValue | null>(null);

  const setBoard = (board: TodoBoardResponse) => queryClient.setQueryData(TODO_BOARD_KEY, board);
  const refetch = () => queryClient.invalidateQueries({ queryKey: TODO_BOARD_KEY });

  const save = useMutation({
    mutationFn: (v: {
      title: string;
      dueDate: string | null;
      note: string;
      column: TodoColumnValue;
    }) => {
      const card = dialog.card;
      if (!card) return createTodoCard(v);
      // 取り込んだカードのタスク名と期限は取り込み元が正なので送らない。状態は変えたときだけ送る
      const column = v.column === card.column ? {} : { column: v.column };
      return updateTodoCard(
        card.id,
        card.source
          ? { note: v.note, ...column }
          : { title: v.title, dueDate: v.dueDate, note: v.note, ...column },
      );
    },
    onSuccess: (board) => {
      setBoard(board);
      setDialog((d) => ({ ...d, open: false }));
    },
  });
  const move = useMutation({
    mutationFn: (v: { id: string; column: TodoColumnValue; index: number }) =>
      moveTodoCard(v.id, { column: v.column, index: v.index }),
    // 先に並べ替えて見せ、保存の結果で置き換える。失敗したら取り直す
    onMutate: (v) => {
      const current = queryClient.getQueryData<TodoBoardResponse>(TODO_BOARD_KEY);
      if (current)
        setBoard({ ...current, cards: moveCard(current.cards, v.id, v.column, v.index) });
    },
    onSuccess: setBoard,
    onError: refetch,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteTodoCard(id),
    onSuccess: setBoard,
  });
  const sync = useMutation({ mutationFn: syncTodoBoard, onSuccess: setBoard });
  const disconnect = useMutation({ mutationFn: disconnectBacklog, onSuccess: refetch });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const cards = q.data?.cards ?? [];
  const backlog = q.data?.sources.find((s) => s.type === "BACKLOG") ?? null;
  const columnOfOver = (overId: string): TodoColumnValue | null =>
    (TODO_COLUMNS.find((c) => columnDropId(c.id) === overId)?.id ??
      cards.find((c) => c.id === overId)?.column) ||
    null;
  const onDragStart = (e: DragStartEvent) => setDragging(String(e.active.id));
  const onDragOver = (e: DragOverEvent) =>
    setOverColumn(e.over ? columnOfOver(String(e.over.id)) : null);
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    setOverColumn(null);
    if (!e.over) return;
    const target = dropTarget(cards, String(e.active.id), String(e.over.id));
    if (target) move.mutate({ id: String(e.active.id), ...target });
  };
  const draggingCard = cards.find((c) => c.id === dragging) ?? null;
  const today = jstToday();

  return (
    <HomeSection
      data-testid="todo-board-card"
      className={className}
      title="ToDo"
      description="自分だけのボード。Backlog の担当課題は自動で追加されます"
      action={
        q.data ? (
          <div className="flex items-center gap-1">
            {backlog?.status === "disconnected" && (
              // 見出しの右端に置く(ユーザー要望 2026-10-14)。同意画面からこのタブへ戻す認可の流れなので、新しいタブでは開かない
              <Button asChild size="sm" variant="outline">
                <a
                  href="/api/backlog/connect"
                  title="自分が担当の未完了の課題を自動で追加します(読み取りだけ)"
                >
                  <img src={backlogIcon} alt="" aria-hidden="true" className="size-4" />
                  Backlog を連携する
                </a>
              </Button>
            )}
            {backlog && backlog.status !== "disconnected" && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Backlog から取り込み直す"
                title="Backlog から取り込み直す"
                disabled={sync.isPending}
                onClick={() => sync.mutate()}
              >
                <RefreshCw className={cn(sync.isPending && "animate-spin")} />
              </Button>
            )}
            {backlog && backlog.status !== "disconnected" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="ToDo の設定"
                    title="ToDo の設定"
                  >
                    <MoreVertical />
                  </Button>
                </DropdownMenuTrigger>
                {/* 連携ごとにサブメニューにまとめる(ユーザー要望 2026-10-14)。ほかの連携や設定は、ここに並べて足す */}
                <DropdownMenuContent align="end" className="min-w-48">
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <img src={backlogIcon} alt="" aria-hidden="true" className="size-4" />
                      Backlog 連携
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="min-w-48">
                      <DropdownMenuItem onSelect={() => setProjectsOpen(true)}>
                        <FolderCog />
                        取り込むプロジェクトを選ぶ
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        disabled={disconnect.isPending}
                        onSelect={() => disconnect.mutate()}
                      >
                        <Link2Off />
                        連携を解除
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ) : undefined
      }
    >
      <CardStatus isPending={q.isPending} isError={q.isError} refetch={() => q.refetch()} />
      {backlog && <BacklogStatus source={backlog} />}
      <InlineError error={move.error ?? remove.error ?? sync.error ?? disconnect.error} />
      {q.data && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={() => {
            setDragging(null);
            setOverColumn(null);
          }}
        >
          <div className="grid min-h-0 flex-1 grid-cols-3 gap-3">
            {TODO_COLUMNS.map((col) => (
              <TodoColumn
                key={col.id}
                column={col.id}
                label={col.label}
                cards={cardsOf(cards, col.id)}
                highlighted={dragging !== null && overColumn === col.id}
                today={today}
                onAdd={() => setDialog({ open: true, card: null, column: col.id })}
                onEdit={(card) => setDialog({ open: true, card, column: card.column })}
                onMove={(card, column) =>
                  move.mutate({ id: card.id, column, index: cardsOf(cards, column).length })
                }
                onRemove={(card) => remove.mutate(card.id)}
              />
            ))}
          </div>
          <DragOverlay>
            {draggingCard && <TodoCardBody card={draggingCard} today={today} overlay />}
          </DragOverlay>
        </DndContext>
      )}
      <BacklogProjectsDialog open={projectsOpen} onOpenChange={setProjectsOpen} />
      <TodoCardDialog
        open={dialog.open}
        onOpenChange={(open) => {
          if (!open) save.reset();
          setDialog((d) => ({ ...d, open }));
        }}
        card={dialog.card}
        column={dialog.column}
        onSubmit={(v) => save.mutate(v)}
        pending={save.isPending}
        error={save.error}
      />
    </HomeSection>
  );
}

/**
 * Backlog の連携の状態。最後に取り込んだ日時か、失敗したら前回までの内容を出していることを伝える。
 * 未連携なら何も出さない(連携ボタンは見出しの右端に置く。2026-10-14)。
 */
function BacklogStatus({ source }: { source: TodoSourceView }) {
  if (source.status === "disconnected") return null;
  return (
    <p role="status" className="text-xs text-muted-foreground">
      {source.status === "error" ? (
        <span className="text-destructive">
          Backlog から取り込めませんでした。前回までの内容を表示しています。
        </span>
      ) : (
        source.syncedAt && `Backlog: ${time.format(new Date(source.syncedAt))} に取り込み`
      )}
      {source.skipped > 0 && (
        <span className="text-destructive">
          {" "}
          カードが上限に達したため、{source.skipped} 件を追加できませんでした。
        </span>
      )}
    </p>
  );
}

function TodoColumn({
  column,
  label,
  cards,
  highlighted,
  today,
  onAdd,
  onEdit,
  onMove,
  onRemove,
}: {
  column: TodoColumnValue;
  label: string;
  cards: TodoCardView[];
  highlighted: boolean;
  today: string;
  onAdd: () => void;
  onEdit: (card: TodoCardView) => void;
  onMove: (card: TodoCardView, column: TodoColumnValue) => void;
  onRemove: (card: TodoCardView) => void;
}) {
  const { setNodeRef } = useDroppable({ id: columnDropId(column) });
  return (
    <section
      aria-label={label}
      data-testid={`todo-column-${column}`}
      className={cn(
        "flex min-h-0 min-w-0 flex-col bg-muted/60",
        highlighted && "outline-2 outline-ring",
      )}
    >
      <div className="flex items-center justify-between gap-1 pt-1 pr-0.5 pb-0.5 pl-2">
        <h3 className="flex items-baseline gap-1.5 text-xs font-semibold">
          {label}
          <span className="font-normal text-muted-foreground tabular-nums">{cards.length}</span>
        </h3>
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-7"
          aria-label={`${label}に追加`}
          title={`${label}に追加`}
          onClick={onAdd}
        >
          <Plus />
        </Button>
      </div>
      <SortableContext items={cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        <ul
          ref={setNodeRef}
          className="flex max-h-80 min-h-16 flex-col gap-1.5 overflow-y-auto px-1.5 pb-1.5 lg:max-h-none lg:flex-1"
        >
          {cards.map((card) => (
            <TodoCardItem
              key={card.id}
              card={card}
              today={today}
              onEdit={() => onEdit(card)}
              onMove={(to) => onMove(card, to)}
              onRemove={() => onRemove(card)}
            />
          ))}
        </ul>
      </SortableContext>
    </section>
  );
}

function TodoCardItem({
  card,
  today,
  onEdit,
  onMove,
  onRemove,
}: {
  card: TodoCardView;
  today: string;
  onEdit: () => void;
  onMove: (column: TodoColumnValue) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });
  return (
    <li
      ref={setNodeRef}
      data-testid="todo-card"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn("cursor-grab touch-none", isDragging && "opacity-40")}
      {...attributes}
      {...listeners}
      aria-label={`${card.title}(ドラッグで移動)`}
    >
      <TodoCardBody
        card={card}
        today={today}
        menu={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="-mt-1 -mr-1 size-7"
                aria-label={`${card.title} のメニュー`}
                title="メニュー"
                // カードのドラッグを始めずに、メニューを開く
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
              {TODO_COLUMNS.filter((c) => c.id !== card.column).map((c) => (
                <DropdownMenuItem key={c.id} onSelect={() => onMove(c.id)}>
                  <ArrowRight />
                  {columnLabel(c.id)}へ移動
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil />
                編集
              </DropdownMenuItem>
              {card.source && (
                <DropdownMenuItem asChild>
                  <a href={card.source.url} target="_blank" rel="noopener noreferrer">
                    <SquareArrowOutUpRight />
                    Backlog で開く
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={onRemove}>
                <Trash2 />
                削除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
    </li>
  );
}

/** カードの中身。ドラッグ中の影(DragOverlay)でも使う。 */
function TodoCardBody({
  card,
  today,
  menu,
  overlay = false,
}: {
  card: TodoCardView;
  today: string;
  menu?: ReactNode;
  overlay?: boolean;
}) {
  const due = card.dueDate ? dueLabel(card.dueDate, today) : null;
  const done = card.column === "DONE";
  return (
    <div className={cn("flex flex-col gap-1 border bg-card p-2 text-sm", overlay && "shadow-md")}>
      <div className="flex items-start justify-between gap-1">
        {card.source ? (
          <a
            href={card.source.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("min-w-0 break-words hover:underline", done && "text-muted-foreground")}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {card.title}
          </a>
        ) : (
          <span className={cn("min-w-0 break-words", done && "text-muted-foreground")}>
            {card.title}
          </span>
        )}
        {menu}
      </div>
      {card.note && (
        <p className="line-clamp-2 text-xs whitespace-pre-wrap text-muted-foreground">
          {card.note}
        </p>
      )}
      {(card.source || due) && (
        <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          {card.source && (
            <span className="inline-flex items-center gap-1" title={card.source.project ?? ""}>
              <img src={backlogIcon} alt="Backlog" className="size-3.5" />
              {card.source.externalId}
            </span>
          )}
          {card.source?.status && <span>・{card.source.status}</span>}
          {card.source?.priority === "HIGH" && <Badge variant="warning">優先度 高</Badge>}
          {due && (
            <Badge
              variant={
                done
                  ? "neutral"
                  : due.tone === "overdue"
                    ? "warning"
                    : due.tone === "soon"
                      ? "info"
                      : "neutral"
              }
            >
              {due.text}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
